import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProductList from '../components/ProductList';
import SearchAutocomplete from '../components/SearchAutocomplete';
import FeaturedListings from '../components/FeaturedListings';
import RecentlyViewed from '../components/RecentlyViewed';
import CartButton from '../components/CartButton';
import AlertBuilder from '../components/AlertBuilder';
import NewsletterSignup from '../components/NewsletterSignup';
import AmazonPromoIcon from '../components/AmazonPromoIcon';
import styles from '../styles/Home.module.css';
import { categories, featuredStores } from '../data/catalog';
import { AMAZON_PROMOS } from '../data/amazonPromos';
import { prefetchDealsList } from '../utils/requestCache';
import comparisonsList from '../data/comparisons.json';

// El usuario detecto que las promos de Amazon (AMAZON_PROMOS, 9 en total)
// solo aparecian intercaladas muy hondo en el scroll infinito (1 de cada 3
// huecos, cada 15 productos -- en la practica, sobre la posicion 45), asi
// que casi nadie llegaba a verlas. Estas 3 (la mas transversal -- envio
// gratis --, la de mas contenido para ver ya mismo, y la tienda propia
// curada, que es la que mas encaja con alguien que ya esta buscando
// gangas) se destacan aparte, cerca de arriba, ademas de seguir rotando
// abajo para quien no las vea aqui.
const HOME_FEATURED_AMAZON_PROMO_IDS = ['prime', 'prime-video', 'tienda'];
const homeFeaturedAmazonPromos = HOME_FEATURED_AMAZON_PROMO_IDS
    .map((id) => AMAZON_PROMOS.find((promo) => promo.id === id))
    .filter(Boolean);

const HOT_ROTATE_MS = 6500;
const HERO_DEAL_COUNT = 5;

// Chips clicables debajo del buscador -- misma idea que ya llevaba el
// placeholder ("Buscar Xiaomi, freidora, vuelos, Druni...") pero ahora se
// puede pulsar en vez de tener que escribirlo: quita la "pereza de buscar"
// (pedido real del usuario, sesion 2026-09-13). Terminos concretos y
// variados a proposito (no nombres de categoria abstractos, que ya tienen
// su propio bloque mas abajo en la home) -- cosas que apetece pulsar de un
// vistazo, cubriendo varias categorias reales del catalogo.
const SUGGESTED_SEARCH_TERMS = ['Auriculares bluetooth', 'Freidora de aire', 'Zapatillas running', 'Perfume', 'Maleta de viaje', 'Robot aspirador'];

const categoryInsights = [
    { slug: 'tecnologia', name: 'Tecnología', signal: 'Móviles, gadgets y accesorios', copy: 'Compara precio actual, descuento y tienda antes de abrir la oferta.' },
    { slug: 'hogar', name: 'Hogar', signal: 'Cocina, orden y herramientas', copy: 'Ideas prácticas para casa con precios fáciles de revisar.' },
    { slug: 'belleza', name: 'Belleza', signal: 'Perfumes y cuidado personal', copy: 'Ofertas que cambian a menudo para comprar cuando el precio baja.' },
    { slug: 'moda', name: 'Moda', signal: 'Zapatillas, ropa y outlet', copy: 'Rebajas con descuento visible y acceso directo a la tienda.' },
    { slug: 'viajes', name: 'Viajes', signal: 'Vuelos, hoteles y escapadas', copy: 'Entra directo a ofertas por tipo de viaje y presupuesto.' }
];

const homeBestOfLinks = [
    { slug: 'tecnologia', name: 'Tecnología', copy: 'Portátiles, móviles y accesorios con descuento real.' },
    { slug: 'hogar', name: 'Hogar', copy: 'Cocina, limpieza y descanso al mejor precio.' },
    { slug: 'belleza', name: 'Belleza', copy: 'Perfumes y cuidado personal rebajados.' },
    { slug: 'moda', name: 'Moda', copy: 'Zapatillas, ropa y outlet de temporada.' }
];
const highlightedCategorySlugs = new Set(categoryInsights.map((category) => category.slug));

const Home = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [hotDeals, setHotDeals] = useState(null);
    const [activeHotIndex, setActiveHotIndex] = useState(0);
    const [carouselPaused, setCarouselPaused] = useState(false);
    const [carouselInteractionPaused, setCarouselInteractionPaused] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [topFeaturedIds, setTopFeaturedIds] = useState([]);
    const [popularStores, setPopularStores] = useState([]);

    useEffect(() => {
        let active = true;

        // Se piden 24 (en vez de los 8 que se muestran) del mismo snapshot
        // mixto de la home y se conserva aqui el ultimo guard por tienda para
        // que tampoco el carrusel pueda repetir una sola marca cinco veces.
        fetch('/api/home/hero')
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error('API unavailable'))))
            .then((data) => {
                if (active && Array.isArray(data) && data.length > 0) {
                    setHotDeals(diversifyByStore(data, 8));
                }
            })
            .catch(() => {
                if (active) {
                    setHotDeals([]);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetch('/api/stores', { signal: controller.signal })
            .then((response) => response.ok ? response.json() : Promise.reject(new Error('Stores API unavailable')))
            .then((data) => {
                if (!Array.isArray(data)) return;
                const bySlug = new Map(data.map((store) => [store.slug, store]));
                const preferred = featuredStores.map((store) => bySlug.get(store.slug)).filter(Boolean);
                const preferredSlugs = new Set(preferred.map((store) => store.slug));
                setPopularStores([...preferred, ...data.filter((store) => !preferredSlugs.has(store.slug))].slice(0, 8));
            })
            .catch((error) => {
                if (error.name !== 'AbortError') setPopularStores([]);
            });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mediaQuery) return undefined;
        const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
        updatePreference();
        mediaQuery.addEventListener?.('change', updatePreference);
        return () => mediaQuery.removeEventListener?.('change', updatePreference);
    }, []);

    const visibleHotDeals = useMemo(
        () => hotDeals ? hotDeals.slice(0, HERO_DEAL_COUNT) : hotDeals,
        [hotDeals]
    );

    useEffect(() => {
        if (!visibleHotDeals || visibleHotDeals.length < 2 || carouselPaused || carouselInteractionPaused || prefersReducedMotion) {
            return undefined;
        }

        const timer = setInterval(() => {
            setActiveHotIndex((index) => (index + 1) % visibleHotDeals.length);
        }, HOT_ROTATE_MS);

        return () => clearInterval(timer);
    }, [visibleHotDeals, carouselPaused, carouselInteractionPaused, prefersReducedMotion]);

    const activeHotDeal = visibleHotDeals ? visibleHotDeals[activeHotIndex % visibleHotDeals.length] : null;

    const handleBoardBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setCarouselInteractionPaused(false);
        }
    };

    useEffect(() => {
        // Debe coincidir con server/lib/seo.js (defaultSeo/DEFAULT_DESCRIPTION)
        // -- ver el mismo bug de drift corregido en DealLanding.js el 2026-08-18.
        document.title = 'Esganga | Gangas, ofertas y descuentos verificados cada día';

        const description = 'Gangas online, ofertas verificadas, códigos descuento y descuentos en tecnología, hogar, belleza, moda y viajes. Ahorra con Esganga.';
        const metaDescription = document.querySelector('meta[name="description"]');

        if (metaDescription) {
            metaDescription.setAttribute('content', description);
        }
    }, []);

    function formatDiscountLabel(discount) {
        if (typeof discount === 'string' && discount.trim()) {
            return discount;
        }
        const value = Number(discount || 0);
        return value > 0 ? `-${value}%` : 'Oferta';
    }

    // Fire-and-forget, mismo criterio que el resto de tracking de clics de
    // afiliado del proyecto -- nunca debe retrasar la navegacion real.
    const handleAmazonPromoClick = (promoId) => {
        fetch(`/api/promo/${promoId}/click`, { method: 'POST' }).catch(() => {});
    };

    const handleSearch = (event) => {
        event.preventDefault();
        const query = searchQuery.trim();

        if (query) {
            navigate(`/buscar?q=${encodeURIComponent(query)}`);
        }
    };

    return (
        <main className={styles.homePage}>
            <div className={styles.heroBand}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <p className={styles.kicker}>Gangas online seleccionadas</p>
                    <h1>Encuentra ofertas buenas sin revisar mil tiendas</h1>
                    <p className={styles.heroText}>
                        Busca un producto, mira el descuento, revisa la tienda y abre solo las ofertas que
                        tienen sentido para comprar ahora.
                    </p>
                    <form className={styles.heroSearch} onSubmit={handleSearch}>
                        <SearchAutocomplete
                            value={searchQuery}
                            onChange={setSearchQuery}
                            onSubmit={(query) => navigate(`/buscar?q=${encodeURIComponent(query)}`)}
                            placeholder="Buscar Xiaomi, freidora, vuelos, Druni..."
                            ariaLabel="Buscar gangas"
                        />
                        <button type="submit">Buscar ganga</button>
                    </form>
                    <div className={styles.suggestedSearches} aria-label="Ideas de busqueda">
                        {SUGGESTED_SEARCH_TERMS.map((term) => (
                            <button
                                key={term}
                                type="button"
                                className={styles.suggestedSearchChip}
                                onClick={() => navigate(`/buscar?q=${encodeURIComponent(term)}`)}
                            >
                                {term}
                            </button>
                        ))}
                    </div>
                    <p className={styles.needsFinderLink}>
                        ¿No sabes por dónde empezar? <Link to="/buscador-inteligente">Cuéntanos qué necesitas</Link>.
                    </p>
                    <div className={styles.trustRow} aria-label="Datos clave de Esganga">
                        <span><strong>Popular</strong> ofertas destacadas</span>
                        <span><strong>-50%</strong> descuentos claros</span>
                        <span><strong>Alertas</strong> cuando baje el precio</span>
                    </div>
                </div>
                <div
                    className={styles.dealBoard}
                    onMouseEnter={() => setCarouselInteractionPaused(true)}
                    onMouseLeave={() => setCarouselInteractionPaused(false)}
                    onFocusCapture={() => setCarouselInteractionPaused(true)}
                    onBlurCapture={handleBoardBlur}
                >
                    {activeHotDeal ? (
                        <div className={styles.boardMain} aria-label="Oferta destacada">
                            <Link
                                key={activeHotDeal.id}
                                to={`/ofertas/${activeHotDeal.id}`}
                                state={{ deal: activeHotDeal }}
                                className={styles.boardImage}
                            >
                                <img src={activeHotDeal.image} alt={activeHotDeal.name} loading="eager" decoding="async" fetchpriority="high" />
                                <strong>{formatDiscountLabel(activeHotDeal.discount)}</strong>
                            </Link>
                            <div className={styles.boardDeal}>
                                <span>{activeHotDeal.store}</span>
                                <strong>{activeHotDeal.name}</strong>
                                <em>{Number(activeHotDeal.price || 0).toFixed(2)}€</em>
                                <Link to={`/ofertas/${activeHotDeal.id}`} state={{ deal: activeHotDeal }}>Ver oferta</Link>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.boardMain} aria-hidden="true">
                            <div className={styles.boardImageSkeleton} />
                            <div className={styles.boardDealSkeleton}>
                                <span className={styles.skeletonBlock} />
                                <span className={styles.skeletonBlock} />
                                <span className={styles.skeletonBlock} />
                                <span className={styles.skeletonBlock} />
                            </div>
                        </div>
                    )}
                    {visibleHotDeals && visibleHotDeals.length > 1 ? (
                        <div className={styles.boardList} aria-label="Seleccionar oferta destacada">
                            {!prefersReducedMotion && (
                                <div className={styles.boardListHeader}>
                                    <button
                                        type="button"
                                        className={styles.boardPause}
                                        aria-pressed={carouselPaused}
                                        aria-label={carouselPaused ? 'Reanudar carrusel' : 'Pausar carrusel'}
                                        title={carouselPaused ? 'Reanudar carrusel' : 'Pausar carrusel'}
                                        onClick={() => {
                                            if (carouselPaused) setCarouselInteractionPaused(false);
                                            setCarouselPaused(!carouselPaused);
                                        }}
                                    >
                                        <span aria-hidden="true">{carouselPaused ? '▶' : 'Ⅱ'}</span>
                                    </button>
                                </div>
                            )}
                            {visibleHotDeals.map((deal, index) => (
                                <button
                                    key={deal.id}
                                    type="button"
                                    className={index === activeHotIndex ? styles.boardItemActive : styles.boardItem}
                                    aria-pressed={index === activeHotIndex}
                                    aria-label={`Mostrar oferta ${index + 1}: ${deal.name}`}
                                    onClick={() => {
                                        setActiveHotIndex(index);
                                        setCarouselPaused(true);
                                    }}
                                >
                                    <img src={deal.image} alt="" />
                                    <span>{deal.store}</span>
                                    <strong>{truncate(deal.name, 54)}</strong>
                                    <em>{Number(deal.price || 0).toFixed(2)}€</em>
                                </button>
                            ))}
                        </div>
                    ) : !hotDeals && (
                        <div className={styles.boardList} aria-hidden="true">
                            {[0, 1, 2, 3, 4].map((key) => (
                                <div key={key} className={styles.boardItemSkeleton}>
                                    <span className={styles.skeletonBlock} style={{ width: 46, height: 46, gridRow: 'span 3' }} />
                                    <span className={styles.skeletonBlock} style={{ width: '35%', height: 12 }} />
                                    <span className={styles.skeletonBlock} style={{ width: '85%', height: 35 }} />
                                    <span className={styles.skeletonBlock} style={{ width: '45%', height: 15 }} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
            </div>

            <FeaturedListings onLoadedIds={setTopFeaturedIds} />

            <RecentlyViewed />

            {homeFeaturedAmazonPromos.length > 0 && (
                <section className={styles.amazonBand} aria-label="Ventajas de comprar con Amazon">
                    <div className={styles.sectionIntro}>
                        <p className={styles.kicker}>Tambien con Amazon</p>
                        <h2>Ventajas que compensan aparte de las gangas</h2>
                        <p>Envio rapido, contenido y nuestra propia seleccion de productos, por si te interesa ademas de lo que ya buscas aqui.</p>
                    </div>
                    <div className={styles.valueGrid}>
                        {homeFeaturedAmazonPromos.map((promo) => (
                            <a
                                key={promo.id}
                                href={promo.href}
                                target="_blank"
                                rel="noopener noreferrer sponsored"
                                onClick={() => handleAmazonPromoClick(promo.id)}
                                className={styles.amazonValueCard}
                                aria-label={`${promo.cta}: ${promo.title}`}
                            >
                                <span><AmazonPromoIcon id={promo.id} /></span>
                                <h3>{promo.title}</h3>
                                <p>{promo.description}</p>
                                <em>{promo.cta} →</em>
                            </a>
                        ))}
                    </div>
                </section>
            )}

            <ProductList
                eyebrow="Lo mejor de las últimas 24 horas"
                title="Novedades top de todas las categorías"
                description="Una mezcla diaria de bajadas de precio, descuentos y ofertas populares, sin dejar que una sola categoría ocupe la portada."
                initialSort="recent"
                inlineFeatured
                inlineFeaturedExcludeIds={topFeaturedIds}
                priorityFirstImage={false}
                pageSize={12}
                autoLoadMore
            />

            <section className={styles.searchBand} aria-label="Búsqueda de gangas">
                <div>
                    <p className={styles.bandLabel}>Todo a la vista</p>
                    <h2>Precio, descuento y tienda antes de hacer clic</h2>
                </div>
                <div className={styles.signalGrid}>
                    <span><strong>Precio</strong> actual y anterior</span>
                    <span><strong>Descuento</strong> explicado en la tarjeta</span>
                    <span><strong>Detalles</strong> para revisar mejor la compra</span>
                </div>
            </section>

            <section className={styles.categoryBoard} aria-label="Categorías principales">
                <div className={styles.sectionIntro}>
                    <p className={styles.kicker}>Compra por categoría</p>
                    <h2>Empieza por lo que necesitas</h2>
                    <p>
                        Elige una categoría y verás solo ofertas relacionadas, con filtros para ordenar por
                        descuento, precio o novedades.
                    </p>
                </div>
                <div className={styles.categoryGrid}>
                    {categoryInsights.map((category) => (
                        <Link
                            key={category.slug}
                            to={`/chollos/${category.slug}`}
                            onMouseEnter={() => prefetchDealsList({ category: category.slug })}
                            onFocus={() => prefetchDealsList({ category: category.slug })}
                        >
                            <span>{category.signal}</span>
                            <strong>{category.name}</strong>
                            <em>{category.copy}</em>
                        </Link>
                    ))}
                </div>
                <div className={styles.categoryStrip}>
                    {categories.filter((category) => !highlightedCategorySlugs.has(category.slug)).map((category) => (
                        <Link
                            key={category.slug}
                            to={`/chollos/${category.slug}`}
                            onMouseEnter={() => prefetchDealsList({ category: category.slug })}
                            onFocus={() => prefetchDealsList({ category: category.slug })}
                        >{category.name}</Link>
                    ))}
                </div>
            </section>

            <section className={styles.bestOfSection} aria-labelledby="home-bestof-title">
                <div className={styles.sectionIntro}>
                    <p className={styles.kicker}>Rankings editoriales</p>
                    <h2 id="home-bestof-title">Mejores ofertas por categoría</h2>
                    <p>
                        Selecciones cortas de lo mejor de cada categoría hoy, con precio, descuento y tienda
                        visibles antes de abrir nada.
                    </p>
                </div>
                <div className={styles.bestOfGrid}>
                    {homeBestOfLinks.map((item) => (
                        <Link key={item.slug} to={`/mejores/${item.slug}`} className={styles.bestOfCard}>
                            <span>Mejores de</span>
                            <strong>{item.name}</strong>
                            <em>{item.copy}</em>
                        </Link>
                    ))}
                </div>
            </section>

            <section className={styles.comparisonsSection} aria-labelledby="home-comparisons-title">
                <div className={styles.sectionIntro}>
                    <p className={styles.kicker}>Comparativas prácticas</p>
                    <h2 id="home-comparisons-title">¿Dudas entre dos productos?</h2>
                    <p>
                        Dos alternativas enfrentadas con criterios, tabla, pros, contras y veredicto para decidir
                        sin equivocarte.
                    </p>
                </div>
                <div className={styles.comparisonsCloud}>
                    {comparisonsList.map((item) => (
                        <Link key={item.slug} to={`/comparativas/${item.slug}`} className={styles.comparisonChip}>
                            {item.title.split(':')[0]}
                        </Link>
                    ))}
                </div>
            </section>

            <NewsletterSignup />

            <section className={styles.gamesTeaser} aria-label="Minijuegos de Esganga">
                <div className={styles.sectionIntro}>
                    <p className={styles.kicker}>Un descanso</p>
                    <h2>¿Un minuto libre? Pon a prueba tu ojo para los chollos</h2>
                    <p>
                        Juegos rapidos con productos y precios reales del catalogo -- nada inventado, solo
                        para pasar el rato mientras decides que comprar.
                    </p>
                </div>
                <Link to="/juegos" className={styles.gamesTeaserCard}>
                    <span className={styles.gamesTeaserEmojis} aria-hidden="true">⚖️ 🎡 🧠 🎯 ⚡</span>
                    <span className={styles.gamesTeaserCta}>Ver los juegos</span>
                </Link>
            </section>

            {popularStores.length > 0 && <section className={styles.storeSection} id="tiendas">
                <div className={styles.sectionIntro}>
                    <p className={styles.kicker}>Tiendas populares</p>
                    <h2>Ve directo a las tiendas que mas se buscan</h2>
                    <p>
                        Si ya tienes una tienda en mente, entra por aqui y revisa sus ofertas activas sin
                        mezclar productos de otras marcas.
                    </p>
                </div>
                <div className={styles.storeCloud}>
                    {popularStores.map((store) => (
                        <Link
                            key={store.slug}
                            to={`/tiendas/${store.slug}`}
                            onMouseEnter={() => prefetchDealsList({ store: store.slug })}
                            onFocus={() => prefetchDealsList({ store: store.slug })}
                        >{store.name}</Link>
                    ))}
                </div>
            </section>}

            <section className={styles.newsletter} id="alertas">
                <div>
                    <p className={styles.kicker}>Alertas de gangas</p>
                    <h2>No pierdas una bajada de precio importante</h2>
                    <p>
                        Guarda lo que buscas y revisa coincidencias por producto, categoría y precio máximo.
                        Es útil para compras que puedes esperar unos días.
                    </p>
                </div>
                <AlertBuilder />
            </section>

            <section className={styles.seoContent}>
                <h2>Compra con menos dudas</h2>
                <p>
                    Esganga está pensada para comparar rápido lo básico: precio actual, precio anterior,
                    tienda, categoría y señales de interés. Así decides si abrir la tienda, guardar la oferta
                    o seguir buscando.
                </p>
                <div className={styles.faqGrid}>
                    <article>
                        <h3>¿Cómo sé si una oferta merece la pena?</h3>
                        <p>Empieza por el descuento, compara el precio anterior y revisa la tienda antes de comprar.</p>
                    </article>
                    <article>
                        <h3>¿Y si no quiero comprar hoy?</h3>
                        <p>Guarda la oferta o crea una alerta con el precio máximo que te gustaría pagar.</p>
                    </article>
                    <article>
                        <h3>¿Por dónde empiezo?</h3>
                        <p>Busca una marca concreta o entra por categoría si solo quieres ver oportunidades.</p>
                    </article>
                </div>
            </section>

            <CartButton />
        </main>
    );
};

function truncate(value, maxLength) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? `${text.slice(0, maxLength - 1).trim()}...` : text;
}

// El pool llega ya ordenado por heat_score (la query que lo pide). Como
// mucho 2 productos de la misma tienda para que el hero se vea variado en
// vez de repetitivo; si no hay suficiente variedad se rellena con lo que
// quede en el mismo orden, para no devolver menos de "count".
function diversifyByStore(pool, count) {
    const perStoreCount = new Map();
    const picked = [];
    const leftover = [];

    for (const deal of pool) {
        const seen = perStoreCount.get(deal.storeSlug) || 0;
        if (seen < 2 && picked.length < count) {
            picked.push(deal);
            perStoreCount.set(deal.storeSlug, seen + 1);
        } else {
            leftover.push(deal);
        }
    }

    for (const deal of leftover) {
        if (picked.length >= count) {
            break;
        }
        picked.push(deal);
    }

    return picked.slice(0, count);
}

export default Home;
